import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  StatusBadge,
} from '@osteopro/ui';
import { AlertCircle, Save } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirtyTabSnapshot } from './patient-tabs-context.js';

/**
 * Dialogue unique des modifications non enregistrées. La V1 en avait deux copies presque
 * identiques (fermeture d'onglet et garde de navigation) ; ici les deux appelants passent leurs
 * libellés et leurs actions.
 */

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UnsavedChangesDialogProps {
  open: boolean;
  testId: string;
  title: string;
  description: string;
  discardLabel: string;
  saveLabel: string;
  snapshot: DirtyTabSnapshot[];
  showGroupNames: boolean;
  onCancel: () => void;
  onProceed: () => void;
  saveRegion: (patientId: string, regionKey: string) => Promise<void>;
  discardRegion: (patientId: string, regionKey: string) => void;
}

export function UnsavedChangesDialog({
  open,
  testId,
  title,
  description,
  discardLabel,
  saveLabel,
  snapshot,
  showGroupNames,
  onCancel,
  onProceed,
  saveRegion,
  discardRegion,
}: UnsavedChangesDialogProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Record<string, RowStatus>>({});
  const [savingAll, setSavingAll] = useState(false);

  const rowKey = (patientId: string, regionKey: string) => `${patientId}::${regionKey}`;

  const setStatus = (key: string, status: RowStatus) => {
    setRows((current) => ({ ...current, [key]: status }));
  };

  const saveOne = async (patientId: string, regionKey: string) => {
    const key = rowKey(patientId, regionKey);
    setStatus(key, 'saving');
    try {
      await saveRegion(patientId, regionKey);
      setStatus(key, 'saved');
      return true;
    } catch {
      setStatus(key, 'error');
      return false;
    }
  };

  const saveAll = async () => {
    setSavingAll(true);
    let everythingSaved = true;
    for (const tab of snapshot) {
      for (const region of tab.regions) {
        if (rows[rowKey(tab.patientId, region.key)] === 'saved') continue;
        if (!region.canSave) {
          setStatus(rowKey(tab.patientId, region.key), 'error');
          everythingSaved = false;
          continue;
        }
        if (!(await saveOne(tab.patientId, region.key))) everythingSaved = false;
      }
    }
    setSavingAll(false);
    if (everythingSaved) {
      setRows({});
      onProceed();
    }
  };

  const close = () => {
    setRows({});
    onCancel();
  };

  const proceedWithoutSaving = () => {
    setRows({});
    onProceed();
  };

  const totalRegions = snapshot.reduce((count, tab) => count + tab.regions.length, 0);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <AlertDialogContent className="max-w-2xl" data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-warning" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <div
          className="my-2 max-h-[40vh] space-y-3 overflow-y-auto"
          data-testid="list-dirty-regions"
        >
          {snapshot.map((tab) => (
            <div key={tab.patientId} className="space-y-2">
              {showGroupNames && <p className="text-sm font-semibold">{tab.patientName}</p>}
              {tab.regions.map((region) => {
                const key = rowKey(tab.patientId, region.key);
                const status = rows[key] ?? 'idle';
                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 rounded-md border p-3"
                    data-testid={`row-dirty-region-${region.key}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{region.label}</span>
                        {status === 'saving' && (
                          <StatusBadge
                            size="sm"
                            status="pending"
                            label={t('common:status.saving')}
                          />
                        )}
                        {status === 'saved' && (
                          <StatusBadge
                            size="sm"
                            status="success"
                            label={t('common:status.saved')}
                          />
                        )}
                        {status === 'error' && (
                          <StatusBadge size="sm" status="error" label={t('common:errors.title')} />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 gap-2">
                      {region.canDiscard && status !== 'saved' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={savingAll || status === 'saving'}
                          onClick={() => {
                            discardRegion(tab.patientId, region.key);
                            setStatus(key, 'saved');
                          }}
                          data-testid={`button-region-discard-${region.key}`}
                        >
                          {t('common:actions.discard')}
                        </Button>
                      )}
                      {region.canSave && status !== 'saved' && (
                        <Button
                          type="button"
                          size="sm"
                          disabled={savingAll || status === 'saving'}
                          onClick={() => void saveOne(tab.patientId, region.key)}
                          data-testid={`button-region-save-${region.key}`}
                        >
                          <Save className="mr-1 h-3 w-3" />
                          {t('common:actions.save')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          {totalRegions === 0 && (
            <Badge variant="secondary">{t('shell:tabs.no_pending_changes')}</Badge>
          )}
        </div>

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            onClick={close}
            disabled={savingAll}
            data-testid="button-unsaved-cancel"
          >
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={proceedWithoutSaving}
            disabled={savingAll}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="button-unsaved-discard"
          >
            {discardLabel}
          </AlertDialogAction>
          <AlertDialogAction
            onClick={() => void saveAll()}
            disabled={savingAll || totalRegions === 0}
            data-testid="button-unsaved-save"
          >
            {saveLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
